import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { generateSEOMeta, generateSchemaOrg } from '@/components/base/SEOData';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9q52j4nlsngrm4llrk0';

export default function AboutPage() {
  const [formError, setFormError] = useState<string>('');
  const [formSuccess, setFormSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const seoMeta = generateSEOMeta({
      title: '关于我们',
      description: '膀胱健康知识网——专注泌尿健康科普的专业团队。了解我们的品牌故事、专业背景和联系方式，获取科学的膀胱保健知识。',
      keywords: '膀胱健康,关于我们,健康科普团队,联系方式,泌尿健康',
      canonicalUrl: '/about',
      ogType: 'website',
      ogImage: 'https://readdy.ai/api/search-image?query=Warm%20professional%20team%20concept%20with%20soft%20cream%20and%20golden%20tones%2C%20minimalist%20medical%20aesthetic&width=1200&height=630&seq=og-about&orientation=landscape',
    });

    const existingMeta = document.querySelector('script[data-seo-meta]');
    if (existingMeta) existingMeta.remove();
    const metaScript = document.createElement('script');
    metaScript.setAttribute('data-seo-meta', 'true');
    metaScript.type = 'application/json';
    metaScript.textContent = seoMeta;
    document.head.appendChild(metaScript);

    const schema = generateSchemaOrg({
      '@type': 'Organization',
      name: '膀胱健康知识网',
      url: 'https://bladder-health.example.com',
      description: '专注泌尿健康科普，提供科学的膀胱保健知识和训练方法',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@bladder-health.com',
        availableLanguage: 'Chinese',
      },
    });

    const existingSchema = document.querySelector('script[data-schema-org]');
    if (existingSchema) existingSchema.remove();
    const schemaScript = document.createElement('script');
    schemaScript.setAttribute('data-schema-org', 'true');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = schema;
    document.head.appendChild(schemaScript);

    document.title = '关于我们 - 膀胱健康知识网';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', '膀胱健康知识网——专注泌尿健康科普的专业团队。了解我们的品牌故事、专业背景和联系方式，获取科学的膀胱保健知识。');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess(false);
    setSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const hpValue = (formData.get('phone_alt') as string || '').trim();

    if (hpValue) {
      setFormSuccess(true);
      form.reset();
      setSubmitting(false);
      return;
    }

    formData.delete('phone_alt');

    try {
      const urlParams = new URLSearchParams();
      formData.forEach((value, key) => {
        urlParams.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlParams.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = [
    { value: '200+', label: '专业文章' },
    { value: '50万+', label: '累计读者' },
    { value: '10+', label: '专家团队' },
    { value: '98%', label: '读者好评' },
  ];

  const teamMembers = [
    {
      name: '李医生',
      role: '泌尿科主治医师',
      description: '从事泌尿外科临床工作15年，擅长膀胱功能障碍的诊治与康复指导。',
      imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20doctor%20portrait%20in%20warm%20cream%20and%20golden%20lighting%2C%20friendly%20smile%2C%20clean%20medical%20coat%2C%20minimalist%20studio%20background%2C%20soft%20natural%20light%2C%20approachable%20and%20trustworthy&width=400&height=400&seq=team-dr-li&orientation=squarish',
    },
    {
      name: '王康复师',
      role: '盆底康复治疗师',
      description: '资深盆底康复专家，持有国际盆底康复认证，帮助数千名患者改善盆底功能。',
      imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20therapist%20portrait%20in%20warm%20cream%20and%20soft%20lighting%2C%20gentle%20smile%2C%20clean%20professional%20attire%2C%20minimalist%20studio%20background%2C%20caring%20and%20warm%20presence&width=400&height=400&seq=team-wang&orientation=squarish',
    },
    {
      name: '张营养师',
      role: '注册营养师',
      description: '专注泌尿健康饮食调理，擅长通过个性化饮食方案辅助改善膀胱功能。',
      imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20nutritionist%20portrait%20with%20warm%20cream%20background%2C%20friendly%20expression%2C%20clean%20professional%20look%2C%20minimalist%20studio%20setting%2C%20soft%20natural%20lighting%2C%20approachable&width=400&height=400&seq=team-zhang&orientation=squarish',
    },
  ];

  return (
    <>
      <Navbar />
      <main>
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[{ label: '首页', path: '/' }, { label: '关于我们' }]}
            />
            <div className="pb-8 md:pb-10 text-center">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-[600px] mx-auto">
                专注泌尿健康科普，用专业知识和真诚关怀守护每一份健康
              </p>
            </div>
          </div>
        </div>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
              <div className="w-full lg:w-1/2">
                <div className="w-full h-[300px] md:h-[380px] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20abstract%20wellness%20and%20health%20concept%20with%20flowing%20organic%20shapes%20in%20soft%20cream%20and%20golden%20tones%2C%20minimalist%20medical%20aesthetic%2C%20clean%20healing%20atmosphere%2C%20gentle%20curves%20representing%20care%20and%20wellbeing&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="膀胱健康知识网品牌形象"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                  <p>
                    膀胱健康知识网成立于2020年，由一群来自泌尿科、康复医学和营养学领域的专业人士共同创建。我们深知膀胱健康问题对生活质量的巨大影响，也看到许多患者因缺乏科学知识而延误了最佳干预时机。
                  </p>
                  <p>
                    我们的使命是让科学的泌尿健康知识触手可及。通过专业、易懂、实用的科普内容，帮助大众建立正确的膀胱健康意识，掌握科学的训练和调理方法，从而改善生活质量。
                  </p>
                  <p>
                    四年来，我们已累计发布超过200篇专业健康文章，服务超过50万读者。每一篇文章都由专业团队编写和审核，确保内容的科学性和准确性。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                我们的数据
              </h2>
              <p className="text-sm text-foreground-600">用数字见证我们的坚持与成长</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 text-center">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                专家团队
              </h2>
              <p className="text-sm text-foreground-600">专业背景保障内容品质</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {teamMembers.map((member) => (
                <div key={member.name} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-4 rounded-full overflow-hidden">
                    <img
                      src={member.imageUrl}
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary-500 font-medium mb-3 whitespace-nowrap">{member.role}</p>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-100" id="contact">
          <div className="w-full max-w-[700px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">
                如果您有任何问题或建议，欢迎通过以下方式与我们取得联系
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 md:mb-10">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900 mb-1 whitespace-nowrap">电子邮箱</p>
                <p className="text-xs text-foreground-600">contact@bladder-health.com</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-50 text-accent-500 mb-3">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900 mb-1 whitespace-nowrap">咨询热线</p>
                <p className="text-xs text-foreground-600">400-123-4567</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500 mb-3">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <p className="text-sm font-medium text-foreground-900 mb-1 whitespace-nowrap">工作时间</p>
                <p className="text-xs text-foreground-600">周一至周五 9:00-18:00</p>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4 text-center">
                给我们留言
              </h3>
              <form onSubmit={handleSubmit} data-readdy-form="">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言（不超过500字）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-hp"
                />

                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-2.5 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? '提交中...' : '提交留言'}
                  </button>
                  {formError && (
                    <p className="text-xs text-accent-500 text-center">{formError}</p>
                  )}
                  {formSuccess && (
                    <p className="text-xs text-secondary-600 text-center">留言已成功提交，我们会尽快回复您！</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}